// A log cabin: timber walls under a pitched gable roof, with a door, a window
// and a stone chimney once finished. Ported 1:1 from age-of-mcp's house.ts: a
// per-stage partsFor() transcribing its stage() logic, with the shared
// foundationSlab/scaffold helpers from the library.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, smokePlume, EFFECT_LOOP, type Part } from "../../lib";

const WALL = "#8b6438";
const ROOF = "#7a4a26";
const RIDGE = "#4a2c18";

// The "hearth is lit" cue: a small smoke plume rising from the stone chimney
// (its mouth tops out at y = 1.18 on the finished house).
const plume = smokePlume([0.27, 1.18, -0.18], { rise: 0.32, radius: 0.05 });

// Stacked-log walls `h` tiles tall, standing on the foundation.
function walls(h: number): Part {
  return {
    geometry: new THREE.BoxGeometry(0.78, h, 0.78),
    color: WALL,
    position: [0, 0.14 + h / 2, 0],
  };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), walls(0.3), ...scaffold(0.84, 0.6)];
  if (s === 2) return [foundationSlab(0.86), walls(0.6), ...scaffold(0.84, 1.05)];

  const wallH = 0.66;
  const top = 0.14 + wallH;
  const rise = 0.34;
  const halfD = 0.46;
  const slope = Math.hypot(halfD, rise);
  const ang = Math.atan2(rise, halfD);
  return [
    foundationSlab(0.86),
    walls(wallH),
    { geometry: new THREE.BoxGeometry(0.96, 0.06, slope), color: ROOF,
      position: [0, top + rise / 2, halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.96, 0.06, slope), color: ROOF,
      position: [0, top + rise / 2, -halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(1.0, 0.07, 0.07), color: RIDGE,
      position: [0, top + rise, 0] },
    { geometry: new THREE.BoxGeometry(0.22, 0.38, 0.05), color: RIDGE,
      position: [0, 0.14 + 0.19, 0.39] },
    { geometry: new THREE.BoxGeometry(0.2, 0.18, 0.05), color: "#c8d7e2",
      position: [0.26, 0.14 + 0.44, 0.39] },
    { geometry: new THREE.BoxGeometry(0.13, 0.36, 0.13), color: "#6a6a6a",
      position: [0.27, top + 0.2, -0.18] },
    ...plume.parts,
  ];
}

export const house = defineModel({
  id: "house",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "House",
  tags: ["building","house","low-poly","age-of-mcp","classic","animated"],
  summary: "Low-poly house.",
  description: "A small wooden dwelling.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Hearth lit",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: plume.tracks,
    },
  ],
});
